import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IdeaFilterParams, IdeaSummary } from '@app/models/analytics/feedback/idea-analytics.model';
import { PeerFilterParams, PeerSummary } from '@app/models/analytics/feedback/peer-feedback-analytics.model';
import { PraiseFilterParams, PraiseSummary } from '@app/models/analytics/feedback/praise-analytics.model';
import { RequestFilterParams, RequestSummary } from '@app/models/analytics/feedback/request-feedback-analytics.model';
import { UploadFilterParams, UploadSummary } from '@app/models/analytics/feedback/upload-analytics.model';
import { UnpromptedFilterParams, UnpromptedSummary } from '@app/models/analytics/feedback/unprompted-feedback-analytics.model';
import { GoalAnalyticsFilterParams, GoalAnalyticsItem } from '@app/models/analytics/goal-analytics.model';
import { OneToOneAnalyticsFilterParams, OneToOneAnalyticsItem } from '@app/models/analytics/one-to-one-analytics.model';
import { ReviewAnalytics } from '@app/models/analytics/review-analytics.model';
import { SurveyAnalytics } from '@app/models/analytics/survey-analytics-model';
import { Observable } from 'rxjs';
import { IAnalyticsAPI } from './interfaces/analytics-api.model';

export interface AnalyticsBaseFilter {
  periodStart:       Date;
  periodEnd:         Date;
  siteId?:           number;
  departmentId?:     number;
  managerId?:        number;
  individualUserId?: number;
}

@Injectable()
export class AnalyticsAPIService implements IAnalyticsAPI {

  private readonly BASE_URL = '/api/statistics';

  constructor(
    private http: HttpClient
  ) { }

  // GOAL ANALYTICS

  getGoalAnalytics(filterParams: GoalAnalyticsFilterParams): Observable<GoalAnalyticsItem> {
    const url = `${this.BASE_URL}/goals`;

    let params = new HttpParams();

    // Add all props of GoalAnalyticsFilterParams to params
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key]) {
        if (key === 'periodStart' || key === 'periodEnd') {
          params = params.append(key, filterParams[key].toISOString());
        } else {
          params = params.append(key, filterParams[key]);
        }
      }
    });

    return this.http.get<GoalAnalyticsItem>(url, { params: params });
  }

  getGoalReportGoalData(filterParams: GoalAnalyticsFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/goals/report/goal-data`;

    let params = new HttpParams();

    // Add all props of OneToOneAnalyticsFilterParams to params
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key]) {
        if (key === 'periodStart' || key === 'periodEnd') {
          params = params.append(key, filterParams[key].toISOString());
        } else {
          params = params.append(key, filterParams[key]);
        }
      }
    });

    return this.http.get(url, { responseType: 'blob', params: params });
  }

  getGoalReportGoalCounts(filterParams: GoalAnalyticsFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/goals/report/goal-counts`;

    let params = new HttpParams();

    // Add all props of OneToOneAnalyticsFilterParams to params
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key]) {
        if (key === 'periodStart' || key === 'periodEnd') {
          params = params.append(key, filterParams[key].toISOString());
        } else {
          params = params.append(key, filterParams[key]);
        }
      }
    });

    return this.http.get(url, { responseType: 'blob', params: params });
  }

  //  1:1 ANALYTICS

  getOneToOneAnalytics(filterParams: OneToOneAnalyticsFilterParams): Observable<OneToOneAnalyticsItem> {
    const url = `${this.BASE_URL}/one-to-ones`;

    let params = new HttpParams();

    // Add all props of OneToOneAnalyticsFilterParams to params
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key]) {
        if (key === 'periodStart' || key === 'periodEnd') {
          params = params.append(key, filterParams[key].toISOString());
        } else {
          params = params.append(key, filterParams[key]);
        }
      }
    });

    return this.http.get<OneToOneAnalyticsItem>(url, { params: params });
  }

  getOneToOneOverviewReport(filterParams: OneToOneAnalyticsFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/one-to-ones/report`;

    let params = new HttpParams();

    // Add all props of OneToOneAnalyticsFilterParams to params
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key]) {
        if (key === 'periodStart' || key === 'periodEnd') {
          params = params.append(key, filterParams[key].toISOString());
        } else {
          params = params.append(key, filterParams[key]);
        }
      }
    });

    return this.http.get(url, { responseType: 'blob', params: params });
  }

  // #region - SURVEYS
  getSurveyAnalyticsOverview(): Observable<SurveyAnalytics> {
    const url = `${this.BASE_URL}/surveys`;
    const params = new HttpParams();
    return this.http.get<SurveyAnalytics>(url, {params});
  }

  getSurveyOverviewReport(): Observable<Blob> {
    const url = `${this.BASE_URL}/surveys/report`;
    return this.http.get(url, {responseType: 'blob'});
  }

  // #endregion

  // #region - REVIEW CYCLES
  getReviewAnalyticsForPeriod(periodStart: Date, periodEnd: Date): Observable<ReviewAnalytics> {
    const url = `${this.BASE_URL}/evaluations`;
    const params = new HttpParams()
      .append('periodStart', `${periodStart.toISOString()}`)
      .append('periodEnd', `${periodEnd.toISOString()}`);
    return this.http.get<ReviewAnalytics>(url, {params});
  }

  getReviewAnalyticsForSelectedCycles(selectedCycleIds: number[]): Observable<ReviewAnalytics> {
    const url = `${this.BASE_URL}/evaluations/selected-cycles`;
    return this.http.post<ReviewAnalytics>(url, selectedCycleIds);
  }

  // #endregion

  // #region - FEEDBACK
  getUnpromptedFeedbackAnalytics(filter: UnpromptedFilterParams) {
    const url = `${this.BASE_URL}/feedback/unprompted`;
    const params = this.buildFilterParams(filter);
    return this.http.get<UnpromptedSummary>(url, { params });
  }

  getUnpromptedFeedbackOverviewReport(filter: UnpromptedFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/unprompted/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params: params });
  }

  getPraiseFeedbackAnalytics(filter: PraiseFilterParams): Observable<PraiseSummary> {
    const url = `${this.BASE_URL}/feedback/praise`;
    const params = this.buildFilterParams(filter);
    return this.http.get<PraiseSummary>(url, { params });
  }

  getPraiseFeedbackOverviewReport(filter: PraiseFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/praise/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params });
  }

  /* ---------- IDEAS ---------- */
  getIdeaFeedbackAnalytics(filter: IdeaFilterParams): Observable<IdeaSummary> {
    const url = `${this.BASE_URL}/feedback/ideas`;
    const params = this.buildFilterParams(filter);
    return this.http.get<IdeaSummary>(url, { params });
  }

  getIdeaFeedbackOverviewReport(filter: IdeaFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/ideas/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params });
  }

  /* ---------- PEER ---------- */
  getPeerFeedbackAnalytics(filter: PeerFilterParams): Observable<PeerSummary> {
    const url = `${this.BASE_URL}/feedback/peer`;
    const params = this.buildFilterParams(filter);
    return this.http.get<PeerSummary>(url, { params });
  }

  getPeerFeedbackOverviewReport(filter: PeerFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/peer/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params });
  }

  /* ---------- REQUESTS ---------- */
  getFeedbackRequestAnalytics(filter: RequestFilterParams): Observable<RequestSummary> {
    const url = `${this.BASE_URL}/feedback/requests`;
    const params = this.buildFilterParams(filter);
    return this.http.get<RequestSummary>(url, { params });
  }

  getFeedbackRequestOverviewReport(filter: RequestFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/requests/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params });
  }

  /* ---------- UPLOADS ---------- */
  getUploadFeedbackAnalytics(filter: UploadFilterParams): Observable<UploadSummary> {
    const url = `${this.BASE_URL}/feedback/uploads`;
    const params = this.buildFilterParams(filter);
    return this.http.get<UploadSummary>(url, { params });
  }
  
  getUploadFeedbackOverviewReport(filter: UploadFilterParams): Observable<Blob> {
    const url = `${this.BASE_URL}/feedback/uploads/report`;
    const params = this.buildFilterParams(filter);
    return this.http.get(url, { responseType: 'blob', params });
  }

  // shared method to build filter params for various analytics
  private buildFilterParams<T extends AnalyticsBaseFilter>(filter: T): HttpParams {
    let params = new HttpParams()
      .set('periodStart', filter.periodStart.toISOString())
      .set('periodEnd', filter.periodEnd.toISOString());
  
    (['siteId', 'departmentId', 'managerId', 'individualUserId'] as const)
      .forEach(key => {
        const value = filter[key];             
        if (typeof value === 'number' && value >= 0) {
          params = params.set(key, String(value));
        }
      });
  
    return params;
  }
  
  // #endregion
}
